import React, { useContext, useEffect } from "react";
import styles from "./btn.module.scss";
import IconSpinner from "../icon/IconSpinner";
import BrandIcon from "../brandIcon/BrandIcon";
import { StateContext } from "../../../context/globalContext/context";
import { iGlobalContext } from "../../../context/globalContext/interface";
import { isPartnerBranding } from "../../../utils/partnerBranding";
import { iconClass } from "../../../constant/neutralIcons";

interface ButtonProps {
  children?: React.ReactNode;
  type?: "button" | "submit" | "reset";
  size?: "sm" | "md" | "md_slim" | "lg" | "pill" | "select_fare" | "flight_cost" | "apply_btn" | "refresh_btn" | "full_width";
  variant?:
  | "default"
  | "cancel"
  | "success"
  | "error"
  | "newDefault"
  | "text"
  | "secondary"
  | "transparent"
  | "tertiary"
  | "fullyTransparent"
  | "newTertiary"
  | "custom"
  // Apply / Done / Show results in dialogs, filters and sheets: the brand fill, or
  // the partner's Secondary colour under Theme Studio button.confirm = dark.
  | "confirm";
  disabled?: boolean;
  /**
   * Shows an inline spinner and keeps the button visually "active" (un-dims the
   * disabled state) while an async prerequisite is loading. Non-icon mode only.
   * Pair with `disabled` so the button stays non-clickable while loading.
   */
  loading?: boolean;
  onClick?: (event: any) => void;
  className?: string;
  style?: any;
  onMouseEnter?: any;
  onMouseLeave?: any;
  // Icon-mode props (from BtnWithTextAndIcon)
  btnClassName?: string;
  btnText?: string;
  textInfo?: string;
  preIcon?: any;
  preIconBranded?: boolean;
  preSvgIcon?: any;
  postIcon?: any;
  iconBtn?: string;
  svgIcon?: any;
  iconHeight?: number;
  translateX?: number;
  translateY?: number;
  color?: any;
  preIconPaddingRight?: number;
}

/** Variants drawn on a solid brand / status fill, where an icon keeps its own colour. */
const FILLED_VARIANTS = new Set<string>(["default", "newDefault", "cancel", "success", "error", "custom", "confirm"]);

const AppButton: React.FC<ButtonProps> = ({
  children,
  type = "button",
  size = "md",
  disabled = false,
  loading = false,
  onClick,
  className,
  variant = "default",
  style = {},
  onMouseEnter = () => { },
  onMouseLeave = () => { },
  // Icon-mode props
  btnClassName = "",
  btnText,
  textInfo = "",
  preIcon = "",
  preIconBranded = false,
  preSvgIcon = "",
  postIcon = "",
  iconBtn = "",
  svgIcon = "",
  iconHeight = 17,
  translateX = 0,
  translateY = 0,
  color = "",
  preIconPaddingRight = 10,
}) => {
  const isIconMode = btnText !== undefined || iconBtn || preIcon || preSvgIcon || postIcon || svgIcon || textInfo;
  const { branding }: iGlobalContext = useContext(StateContext);
  const brandPreIcon = preIconBranded && isPartnerBranding(branding);
  // Theme Studio: a dark icon on a button that is not brand-filled follows the
  // partner's Icons colour (empty class on brand-filled / custom buttons).
  const iconOn = (src: unknown) => (FILLED_VARIANTS.has(variant) ? "" : iconClass(src));

  useEffect(() => {
    if (!isIconMode) return;
    const handleTouchStart = () => {};
    document.addEventListener("touchstart", handleTouchStart, true);
    return () => {
      document.removeEventListener("touchstart", handleTouchStart, true);
    };
  }, [isIconMode]);

  if (isIconMode) {
    const btnComputedClassName = `${btnClassName} ${styles.continueBtn} ${preIcon !== "" && styles.preIcon} ${preSvgIcon !== "" && styles.preIcon} ${iconBtn !== "" && styles.iconBtn} ${svgIcon !== "" && styles.iconBtn}`;
    const buttonClassName = `${btnComputedClassName} ${styles.btn} ${styles[`btn-${size}`]} ${styles[`btn-${variant}`]}`;
    return (
      <div className={`${className || ""} ${styles.continueBtnContainer}`}>
        <button
          type={type}
          disabled={disabled}
          onClick={onClick}
          className={buttonClassName}
          style={onMouseEnter ? { color: color } : {}}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
        >
          {svgIcon !== "" && (
            <div style={{ height: `${iconHeight}px`, transform: `translate(${translateX}px, ${translateY}px)` }}>
              {svgIcon}
            </div>
          )}
          {iconBtn !== "" && (
            <img
              src={iconBtn}
              alt="back"
              className={iconOn(iconBtn)}
              style={{ height: `${iconHeight}px`, transform: `translate(${translateX}px, ${translateY}px)` }}
            />
          )}
          {preSvgIcon !== "" && (
            <div style={{ height: `${iconHeight}px`, transform: `translate(${translateX}px, ${translateY}px)` }}>
              {preSvgIcon}
            </div>
          )}
          {preIcon !== "" &&
            (brandPreIcon ? (
              <BrandIcon
                src={preIcon}
                style={{
                  height: `${iconHeight}px`,
                  width: `${iconHeight}px`,
                  marginRight: `${preIconPaddingRight}px`,
                }}
              />
            ) : (
              <img
                src={preIcon}
                alt="back"
                className={iconOn(preIcon)}
                style={{ height: `${iconHeight}px`, paddingRight: `${preIconPaddingRight}px` }}
              />
            ))}
          {btnText}
          {postIcon !== "" && <img src={postIcon} alt="forward" className={iconOn(postIcon)} style={{ height: `${iconHeight}px` }} />}
        </button>
        {textInfo && <p>{textInfo}</p>}
      </div>
    );
  }

  const computedClassName = `${className} ${styles.btn} ${styles[`btn-${size}`]} ${styles[`btn-${variant}`]} ${loading ? styles.btnLoading : ""}`;
  return (
    <button
      type={type}
      disabled={disabled}
      onClick={onClick}
      className={computedClassName}
      style={style}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {loading && <IconSpinner size="sm" className={styles.btnSpinner} />}
      {children}
    </button>
  );
};

export default AppButton;
